import { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation('navigation');
  const sectorsRef = useRef<HTMLDivElement>(null);

  const navItems = [
    { name: t('home'), path: '/' },
    { 
      name: t('solutions'), 
      path: '/solutions',
      submenu: [
        { name: t('erp'), path: '/solutions/erp' },
        { name: t('crm'), path: '/solutions/crm' },
      ]
    },
    { 
      name: t('sectors'), 
      path: '/secteurs',
      submenu: [
        { name: t('hotellerie'), path: '/secteurs/hotellerie' },
        { name: t('beaute'), path: '/secteurs/beaute' },
        { name: t('evenementiel'), path: '/secteurs/evenementiel' },
        { name: t('restauration'), path: '/secteurs/restauration' },
        { name: t('commerce'), path: '/secteurs/commerce' },
        { name: t('immobilier'), path: '/secteurs/immobilier' },
        { name: t('production'), path: '/secteurs/production' },
        { name: t('education'), path: '/secteurs/education' },
        { name: t('sante'), path: '/secteurs/sante' },
        { name: t('transport'), path: '/secteurs/transport' },
        { name: t('finance'), path: '/secteurs/finance' },
        { name: t('administration'), path: '/secteurs/administration' },
        { name: t('prestataireService'), path: '/secteurs/prestataire-service' },
        { name: t('btpConstruction'), path: '/secteurs/btp-construction' },
        { name: t('rhPlacement'), path: '/secteurs/rh-placement' },
        { name: t('cabinetsConseil'), path: '/secteurs/cabinets-conseil' },
        { name: t('videoSurveillance'), path: '/secteurs/video-surveillance' },
        { name: t('atelierGarage'), path: '/secteurs/atelier-garage' },
        { name: t('bulkSms'), path: '/secteurs/bulk-sms' },
      ]
    },
    { name: t('ecosystem'), path: '/ecosystem' },
    { name: t('about'), path: '/about' },
    { name: t('recruitment'), path: '/recrutement' },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const getLinkClass = (path: string) => {
    return isActive(path)
      ? 'text-blue-600 font-semibold'
      : 'text-gray-700 hover:text-blue-600 transition-colors';
  };

  const getMobileLinkClass = (path: string) => {
    return isActive(path)
      ? 'block px-3 py-2 text-blue-600 font-semibold bg-blue-50 rounded transition-colors'
      : 'block px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors';
  };

  const cycleLanguage = () => {
    const currentBase = i18n.language.split('-')[0];
    const newLang = currentBase === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const getLangLabel = () => {
    const base = i18n.language.split('-')[0];
    return base === 'fr' ? 'FR → EN' : 'EN → FR';
  };

  const getLangTitle = () => {
    const base = i18n.language.split('-')[0];
    return base === 'fr' ? 'Switch to English' : 'Passer en Français';
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-blue-700">
              <a href="/" className="cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/dfcab6d8a996275ea038131e02fb9f3a/3166989a593cffa35e1f609d6cd52734.jpeg" 
                  alt="Alpha Solutions" 
                  className="h-12 w-auto"
                />
              </a>
            </h1>
          </div>

          {/* Navigation desktop */}
          <nav className="hidden md:flex space-x-8">
            <a href="/" className={getLinkClass('/')}>
              {t('home')}
            </a>
            <a href="/solutions" className={getLinkClass('/solutions')}>
              {t('solutions')}
            </a>
            <div className="relative group">
              <button className={`flex items-center ${isActive('/secteurs') ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600 transition-colors'}`}>
                {t('sectors')}
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 max-h-96 overflow-y-auto">
                <div className="py-2">
                  <a href="/secteurs/hotellerie" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('hotellerie')}
                  </a>
                  <a href="/secteurs/restauration" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('restauration')}
                  </a>
                  <a href="/secteurs/beaute" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('beaute')}
                  </a>
                  <a href="/secteurs/evenementiel" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('evenementiel')}
                  </a>
                  <a href="/secteurs/immobilier" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('immobilier')}
                  </a>
                  <a href="/secteurs/commerce" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('commerce')}
                  </a>
                  <a href="/secteurs/production" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('production')}
                  </a>
                  <a href="/secteurs/education" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('education')}
                  </a>
                  <a href="/secteurs/sante" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('sante')}
                  </a>
                  <a href="/secteurs/transport" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('transport')}
                  </a>
                  <a href="/secteurs/finance" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('finance')}
                  </a>
                  <a href="/secteurs/administration" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('administration')}
                  </a>
                  <a href="/secteurs/prestataire-service" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('prestataireService')}
                  </a>
                  <a href="/secteurs/btp-construction" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('btpConstruction')}
                  </a>
                  <a href="/secteurs/rh-placement" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('rhPlacement')}
                  </a>
                  <a href="/secteurs/cabinets-conseil" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('cabinetsConseil')}
                  </a>
                  <a href="/secteurs/video-surveillance" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('videoSurveillance')}
                  </a>
                  <a href="/secteurs/atelier-garage" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('atelierGarage')}
                  </a>
                  <a href="/secteurs/bulk-sms" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                    {t('bulkSms')}
                  </a>
                </div>
              </div>
            </div>
            <a href="/about" className={getLinkClass('/about')}>
              {t('about')}
            </a>
            <a href="/recrutement" className={getLinkClass('/recrutement')}>
              {t('recruitment')}
            </a>
          </nav>

          {/* CTA Button & Language Switcher */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={cycleLanguage}
              title={getLangTitle()}
              className="flex items-center space-x-2 px-4 py-2 rounded-full border-2 border-gray-800 hover:bg-gray-800 hover:text-white transition-all duration-200 cursor-pointer group"
            >
              <i className="ri-translate-2 text-gray-800 group-hover:text-white text-base"></i>
              <span className="text-gray-800 group-hover:text-white font-bold text-sm tracking-wide">
                {getLangLabel()}
              </span>
            </button>
            <a 
              href="https://alphasolutions237.bnrerpplus.net/appointly/appointments_public/book" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#F57C20] hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              {t('headerBookRdv')}
            </a>
          </div>

          {/* Mobile: lang button + burger */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={cycleLanguage}
              title={getLangTitle()}
              className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-gray-700 hover:bg-gray-700 hover:text-white transition-all duration-200 cursor-pointer group"
              aria-label={getLangTitle()}
            >
              <span className="text-gray-700 group-hover:text-white font-bold text-xs tracking-wide">
                {i18n.language.split('-')[0].toUpperCase()}
              </span>
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600 transition-colors"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 overflow-y-auto max-h-[80vh]">
            <div className="px-4 py-2 space-y-1">
              <a
                href="/"
                className={getMobileLinkClass('/')}
              >
                {t('home')}
              </a>
              <a
                href="/solutions"
                className={getMobileLinkClass('/solutions')}
              >
                {t('solutions')}
              </a>

              {/* Sectors accordion */}
              <div>
                <button
                  onClick={() => setIsSectorsOpen(!isSectorsOpen)}
                  className={`flex items-center justify-between w-full px-3 py-2 rounded transition-colors cursor-pointer ${
                    isActive('/secteurs') ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{t('sectors')}</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isSectorsOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isSectorsOpen && (
                  <div
                    ref={sectorsRef}
                    className="mt-1 ml-3 border-l-2 border-gray-200 pl-3 max-h-52 overflow-y-auto space-y-0.5"
                  >
                    <a href="/secteurs/hotellerie" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('hotellerie')}</a>
                    <a href="/secteurs/restauration" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('restauration')}</a>
                    <a href="/secteurs/beaute" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('beaute')}</a>
                    <a href="/secteurs/evenementiel" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('evenementiel')}</a>
                    <a href="/secteurs/immobilier" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('immobilier')}</a>
                    <a href="/secteurs/commerce" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('commerce')}</a>
                    <a href="/secteurs/production" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('production')}</a>
                    <a href="/secteurs/education" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('education')}</a>
                    <a href="/secteurs/sante" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('sante')}</a>
                    <a href="/secteurs/transport" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('transport')}</a>
                    <a href="/secteurs/finance" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('finance')}</a>
                    <a href="/secteurs/administration" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('administration')}</a>
                    <a href="/secteurs/prestataire-service" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('prestataireService')}</a>
                    <a href="/secteurs/btp-construction" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('btpConstruction')}</a>
                    <a href="/secteurs/rh-placement" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('rhPlacement')}</a>
                    <a href="/secteurs/cabinets-conseil" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('cabinetsConseil')}</a>
                    <a href="/secteurs/video-surveillance" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('videoSurveillance')}</a>
                    <a href="/secteurs/atelier-garage" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('atelierGarage')}</a>
                    <a href="/secteurs/bulk-sms" className="block py-1.5 px-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded transition-colors">{t('bulkSms')}</a>
                  </div>
                )}
              </div>
              <a
                href="/about"
                className={getMobileLinkClass('/about')}
              >
                {t('about')}
              </a>
              <a
                href="/recrutement"
                className={getMobileLinkClass('/recrutement')}
              >
                {t('recruitment')}
              </a>
              <div className="px-3 pb-3">
                <a
                  href="https://alphasolutions237.bnrerpplus.net/appointly/appointments_public/book"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-[#F57C20] hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap w-full"
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  {t('headerBookRdv')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}